import { Box, useTheme } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";

import { useRegisterSW } from "virtual:pwa-register/react";
import UseTimeouts from "@/hooks/useTimeouts.ts";
import { forceReload } from "@/models/BootstrapUtils.ts";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { hasFeedsInDatabase } from "@/models/repositories";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";

export default function ReloadPrompt() {
  const theme = useTheme();
  const { currentWorkspaceId } = useContext(WorkspaceContext);

  const hasFeeds =
    useReactiveSelectQuery(
      (id: string) => hasFeedsInDatabase(id),
      [currentWorkspaceId],
      "ReloadPrompt:hasFeedsInDatabase",
    ) ?? [];
  const feedsExist = hasFeeds.length > 0;

  const [counter, setCounter] = useState<number>(0);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [showReloadApplication, setShowReloadApplication] = useState<boolean>(false);
  const { refresh } = UseTimeouts(10);

  const reloadApplication = () => {
    setDisabled(true);
    forceReload()
      .then(() => {
        setDisabled(false);
        window.location.reload();
      })
      .catch((e) => {
        setDisabled(false);
        console.log({ e });
        window.location.reload();
      });
  };

  const prevWorkspaceIdRef = useRef(currentWorkspaceId);

  useEffect(() => {
    if (!currentWorkspaceId) {
      return;
    }

    // When the workspace changes, reset and bail out so the rest of the logic
    // never acts on stale snapshot values from the previous workspace.
    if (prevWorkspaceIdRef.current !== currentWorkspaceId) {
      prevWorkspaceIdRef.current = currentWorkspaceId;
      setShowReloadApplication(false);
      setCounter(0);
      return;
    }

    if (feedsExist && showReloadApplication) {
      setShowReloadApplication(false);
      return;
    }
    if (refresh === true && !feedsExist && counter >= 1 && !showReloadApplication) {
      setShowReloadApplication(true);
    }
    if (refresh) {
      setCounter((value) => value + 1);
    }
  }, [refresh, feedsExist, counter, showReloadApplication, currentWorkspaceId]);

  const registerPeriodicSync = (period: number, swUrl: string, r: ServiceWorkerRegistration) => {
    if (period <= 0) return;

    setInterval(async () => {
      if ("onLine" in navigator && !navigator.onLine) {
        return;
      }

      try {
        const resp = await fetch(swUrl, {
          cache: "no-store",
          headers: {
            cache: "no-store",
            "cache-control": "no-cache",
          },
        });

        if (resp?.status === 200) {
          await r.update();
        } else {
          console.log(`%cService Worker Status %c[${resp?.status}]`, "color:#666;", "color:red;");
        }
      } catch (e) {
        console.log(`ERROR LOADING ${swUrl}`);
      }
    }, period);
  };
  const period = 1000 * 30; // check every 30 seconds
  const serviceWorker = {
    onRegisteredSW(swUrl, r) {
      if (period <= 0) return;
      if (r?.active?.state === "activated") {
        registerPeriodicSync(period, swUrl, r);
      } else if (r?.installing) {
        r.installing.addEventListener("statechange", (e) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === "activated") registerPeriodicSync(period, swUrl, r);
        });
      }
    },
  };
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW(serviceWorker);

  const updateAndReload = () => {
    updateServiceWorker();
    setTimeout(() => {
      window.location.href = "/";
    }, 250);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const boxProps = {
    position: "fixed",
    bottom: "0.5rem",
    right: "0.5rem",
    display: "flex",
    gap: 1,
    alignItems: "center",
    fontSize: "14px",
    border: `solid 1px ${theme.palette.secondary.light}`,
    background: theme.palette.secondary.main,
    py: 0.5,
    px: 1,
    borderRadius: 1,
    zIndex: 9999,
  };

  return (
    <Box role="alert" aria-labelledby="toast-message">
      {showReloadApplication && (
        <Box sx={{ ...boxProps }}>
          <button
            disabled={disabled}
            style={{ padding: "0.25rem  0.5rem" }}
            onClick={reloadApplication}
            className="basic-button grays"
          >
            Click To Clear Cache & Reload
          </button>
        </Box>
      )}
      {needRefresh && (
        <Box sx={{ ...boxProps }}>
          <Box>Your version is out of date.</Box>
          <Box>
            <Box
              component="button"
              sx={{
                fontSize: "13px",
                border: "solid 1px #a92afe",
                background: "#8804ff",
                "&:hover": { background: "#6700c3" },
              }}
              className="basic-button "
              onClick={() => updateAndReload()}
            >
              Update
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
